/**
 * Skeleton Component
 *
 * A loading placeholder component with multiple variants.
 * Provides accessible loading states with pulse animation.
 *
 * @example
 * // Default rectangular skeleton
 * <Skeleton className="w-full h-12" />
 *
 * @example
 * // Circular skeleton (avatar placeholder)
 * <Skeleton variant="circular" className="w-12 h-12" />
 *
 * @example
 * // Text line skeleton
 * <Skeleton variant="text" className="w-3/4 h-4" />
 */

import React from "react"
import { cn } from "@/lib/utils"

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Skeleton variant - determines shape and styling
   * @default "default"
   */
  variant?: "default" | "circular" | "text"
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant = "default", ...props }, ref) => {
    const variantClasses = {
      default: "rounded-md",
      circular: "rounded-full",
      text: "rounded-sm h-4"
    }

    return (
      <div
        ref={ref}
        role="status"
        aria-busy="true"
        aria-label="Loading"
        className={cn(
          "animate-pulse bg-muted",
          variantClasses[variant],
          className
        )}
        {...props}
      />
    )
  }
)

Skeleton.displayName = "Skeleton"

export { Skeleton }
